import React from 'react'
import { Link } from 'gatsby'
import { Box, SxProps, Typography, useTheme } from '@mui/material'

import { Icon } from './Icon'
import { PortableText } from './PortableText'

import Image from '@components/Image'
import { rudderslabTrackOnClick } from '~/utils/common'

export type LeftRightImageContentProps = {
  data: {
    leftrightcontentmaintitle: string
    leftrightcontentsmalltitle: string
    header_alignment: string
    showDivider: boolean
    header_media: {
      condition: string
      imageoption: {
        asset: {
          _ref: string
          altText: string
        }
      }
    }
    header_media_cta_cutton: {
      btntext: string
      btnlink: string
      btnhightlight: boolean
      btnexternallink: boolean
    }
    header_feature_items: {
      _key: string
      card_image: {
        asset: {
          _ref: string
          altText: string
        }
      }
      title: string
    }[]
    leftrightimageblock: {
      _key: string
      condition: string
      title: string
      title_color: string
      isTitleBigger: boolean
      smalltitle: string
      content: object
      content_display_ratio: string
      pointer_content_cols: string
      pointer_contents: string[]
      addlink: {
        cmnlinktext: string
        cmnlink: string
        cmnexternallink: boolean
        cmn_display_as_button: boolean
      }
      imageVideoConditionBlock: {
        imageoption: {
          asset: {
            _ref: string
            altText: string
          }
        }
      }
    }[]
  }
  sx?: SxProps
}

export const LeftRightImageContent = ({
  data,
  sx,
}: LeftRightImageContentProps) => {
  const { palette } = useTheme()

  const smallTitle = data?.leftrightcontentsmalltitle || ''
  const mainTitle = data?.leftrightcontentmaintitle || ''
  const headerCtaButton = data?.header_media_cta_cutton

  return (
    <Box
      sx={{
        maxWidth: 1152,
        mx: 'auto',
        pb: { xs: 5, mobile: 12 },
        pt: { xs: 5, mobile: 12 },
        px: { xs: 2, desktop: 0 },
        ...sx,
      }}>
      <Box sx={{ textAlign: data?.header_alignment || '' }}>
        {smallTitle && (
          <Typography sx={{ fontSize: 14, mb: 1 }}>
            {smallTitle.toUpperCase()}
          </Typography>
        )}
        {mainTitle && (
          <Typography
            sx={{
              fontSize: { xs: 40, mobile: 50 },
              mb: 3,
              fontWeight: 700,
            }}>
            {mainTitle}
          </Typography>
        )}
      </Box>

      {data?.header_media?.imageoption && (
        <Box
          sx={{
            maxWidth: 770,
            mx: 'auto',
            my: 5,
            img: { width: '100%' }
          }}
        >
          <Box component={Image} alt={data?.header_media?.imageoption?.asset?.altText} props={data?.header_media?.imageoption?.asset?._ref} />
        </Box>
      )}

      {headerCtaButton?.btntext && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <Box
            component={headerCtaButton.btnexternallink ? 'a' : Link}
            href={headerCtaButton.btnlink}
            to={headerCtaButton.btnlink}
            onClick={e =>
              rudderslabTrackOnClick('button', headerCtaButton.btntext, e)
            }
            sx={{
              width: { xs: '100%', mobile: 300 },
              display: 'inline-block',
            }}>
            <Typography
              variant='subtitle2'
              sx={{
                color: headerCtaButton.btnhightlight
                  ? 'text.primary'
                  : 'background.default',
                fontWeight: 500,
                px: 5,
                py: 2,
                position: 'relative',
                textAlign: 'center',
                zIndex: 1,
                '&:before': {
                  background:
                    'linear-gradient(135.55deg,#3e35d1 6.09%,#86f8e0 116.03%)',
                  borderRadius: 2,
                  bottom: 0,
                  content: '""',
                  left: 0,
                  p: 0.25,
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  WebkitMaskComposite: headerCtaButton.btnhightlight
                    ? 'xor'
                    : 'unset',
                  WebkitMask:
                    'linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0)',
                  zIndex: headerCtaButton.btnhightlight ? 0 : -1,
                },
              }}>
              {headerCtaButton.btntext}
            </Typography>
          </Box>
        </Box>
      )}

      {data?.header_feature_items && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', mobile: 'row' },
            gap: 3,
            justifyContent: 'center',
            mt: 8,
            p: 2,
          }}>
          {data?.header_feature_items.map(item => (
            <Box
              key={item._key}
              sx={{
                width: 264,
                alignItems: 'center',
                display: 'flex',
                flexDirection: { mobile: 'column' },
                img: { width: 40, minHeight: 40, objectFit: 'contain' }
              }}
            >
              <Box component={Image} alt={item.card_image.asset?.altText} props={item.card_image.asset?._ref} />
              <Typography
                sx={{
                  color: 'primary.dark',
                  fontSize: 20,
                  fontWeight: 500,
                  ml: { xs: 2, mobile: 0 },
                  mt: { mobile: 2 },
                }}>
                {item.title}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'grid', gap: { xs: 5, mobile: 8, laptop: 10 } }}>
        {data?.leftrightimageblock.map(item => (
          <Box
            key={item._key}
            sx={{
              alignItems: 'center',
              display: { mobile: 'flex' },
              flexDirection: item.condition === 'right' ? 'row-reverse' : 'row',
            }}>
            <Box
              sx={{
                width: {
                  mobile:
                    item.content_display_ratio === '60:40' ? '60%' : '50%',
                },
                pl: { mobile: item.condition === 'right' ? 8 : 0 },
                pr: { mobile: item.condition === 'right' ? 0 : 1 },
              }}>
              <Typography sx={{ fontSize: 18 }}>
                {item.smalltitle?.toUpperCase()}
              </Typography>

              <Typography
                variant='h2'
                sx={{
                  ...(item.title_color === 'midnightBlue' && {
                    color: 'primary.dark',
                  }),
                  ...(item.isTitleBigger && {
                    fontSize: { xs: 38, mobile: 50 },
                  }),
                }}>
                {item.title}
              </Typography>

              <PortableText
                blocks={item.content}
                trackSectionHeader={item.title}
                sx={{ mt: 3 }}
                sxBlock={{
                  '.MuiTypography-body1': { color: 'text.secondary' },
                }}
              />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    mobile: `repeat(${item.pointer_content_cols}, 1fr)`,
                  },
                }}>
                {item.pointer_contents?.map(pointerItem => (
                  <Box key={pointerItem} sx={{ display: 'flex', mt: 3 }}>
                    <Icon
                      icon='check-circle'
                      color={palette.primary.dark}
                      size='lg'
                      sx={{ pt: 0.25 }}
                    />
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        fontSize: 20,
                        fontWeight: 'bold',
                        ml: 1.5,
                      }}>
                      {pointerItem}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {item.addlink?.cmnlinktext && (
                <Box
                  component={item.addlink.cmnexternallink ? 'a' : Link}
                  href={item.addlink.cmnlink}
                  to={item.addlink.cmnlink}
                  onClick={e => rudderslabTrackOnClick('link', item.title, e)}
                  sx={{
                    width: 'fit-content',
                    alignItems: 'center',
                    borderRadius: 2,
                    display: 'flex',
                    mt: 4,
                    ...(item.addlink.cmn_display_as_button && {
                      background:
                        'linear-gradient(135.55deg, #3e35d1 6.09%, #86f8e0 116.03%)',
                      color: 'background.default',
                      px: 5,
                      py: 2,
                      h6: { color: 'background.default' },
                      img: { display: 'none' },
                    }),
                  }}>
                  <Box
                    component='img'
                    src='/images/arrow-right-black.svg'
                    alt='arrow'
                  />
                  <Typography
                    variant='subtitle2'
                    sx={{ color: 'secondary.main', ml: 1.5 }}>
                    {item.addlink.cmnlinktext}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                width: {
                  mobile:
                    item.content_display_ratio === '60:40' ? '40%' : '50%',
                },
                mt: { xs: 6, mobile: 0 },
                pl: { mobile: item.condition === 'right' ? 0 : 3 },
                pr: { mobile: item.condition === 'right' ? 3 : 0 },
                img: { width: '100%' }
              }}
            >
              <Box component={Image} props={item.imageVideoConditionBlock.imageoption?.asset?._ref} alt={item.imageVideoConditionBlock.imageoption?.asset?.altText}/>
            </Box>
          </Box>
        ))}
      </Box>

      {data?.showDivider && (
        <Box
          sx={{
            position: 'absolute',
            background:
              'linear-gradient(120deg,#070e1f,#3e35d1 25%,#86f8e0 50%,#f3e1fd 75%,#d692f6)',
            height: 10,
            left: 0,
            width: '100%',
            display: 'block',
          }}
        />
      )}
    </Box>
  )
}
